import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type AdminRole = "ADMIN" | "SUPER_ADMIN";

export interface Admin {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
}

interface AuthState {
  token: string | null;
  admin: Admin | null;
  isAuthLoading: boolean;
}

interface JwtPayload {
  exp?: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload)) as JwtPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 >= payload.exp;
}

const savedToken = localStorage.getItem("waa_token");
const hasValidToken = Boolean(savedToken && !isTokenExpired(savedToken));
const savedAdmin: Admin | null = (() => {
  try {
    return JSON.parse(localStorage.getItem("waa_admin") || "null") as Admin | null;
  } catch {
    return null;
  }
})();

if (!hasValidToken) {
  localStorage.removeItem("waa_token");
  localStorage.removeItem("waa_admin");
}

const initialState: AuthState = {
  token: hasValidToken ? savedToken : null,
  admin: hasValidToken ? savedAdmin : null,
  // Si pas de token valide localement, on sait déjà qu'on n'est pas connecté.
  // On n'attend pas l'API → pas de flash du dashboard.
  // Si token valide, on reste en loading le temps de valider côté serveur.
  isAuthLoading: hasValidToken,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<{ token: string; admin: Admin }>) => {
      state.token = action.payload.token;
      state.admin = action.payload.admin;
      state.isAuthLoading = false;
      localStorage.setItem("waa_token", action.payload.token);
      localStorage.setItem("waa_admin", JSON.stringify(action.payload.admin));
    },
    setAdmin: (state, action: PayloadAction<Admin>) => {
      state.admin = action.payload;
      localStorage.setItem("waa_admin", JSON.stringify(action.payload));
    },
    clearAuth: (state) => {
      state.token = null;
      state.admin = null;
      state.isAuthLoading = false;
      localStorage.removeItem("waa_token");
      localStorage.removeItem("waa_admin");
    },
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isAuthLoading = action.payload;
    },
  },
});

export const { setAuth, setAdmin, clearAuth, setAuthLoading } = authSlice.actions;
export default authSlice.reducer;
